
import {Vue, Component, Prop, Watch} from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import {COL, sqlSum, sqlSumByDocType, getTableName} from '@/worker/db/sqlHelpers';
import FilterWidget from '../shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import moment from 'moment';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
    components: {
        AmountDisplayerFilterable,
        IncludeCreditNoteToggle,
        IncludeDebitNoteToggle,
        SlPhModuleOnlyToggle,
    },
})
export default class Agent12MonthsSalesNumberDisplayer extends FilterWidget {
    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
    > = [];

    public salesAmount = 0;

    public customersCount = 0;
    public activeCustomersCount = 0;
    public lastInvoiceDate: number = 0;

    @Watch('customersCount', { immediate: true })
    public onCustomerCountChange(newVal) {
        this.$emit('returnCustomerCount', this.customersCount);
    }

    @Watch('lastInvoiceDate', { immediate: true })
    public onLastInvoiceDateChange(newVal) {
        this.$emit('returnLastInvoiceDate', this.lastInvoiceDate);
    }

    public get oneYearBeforeRange(): [number, number] {
        return [
            moment(this.selectedAsOfDate).add(-1, 'year').add(1, 'month').startOf('month').valueOf(),
            moment(this.selectedAsOfDate).endOf('month').valueOf(),
        ];
    }

    public get includeCreditNote() {
        return settingx.now.includeCreditNote;
    }

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public get slPhModuleOnly() {
        return settingx.now.slPhModuleOnly;
    }

    public get permissionIds(): PermissionsGroup[] {
        return ['customers', 'sales'];
    }

    public get expensiveHook() {
        const {
            selectedDateRange,
            selectedAsOfDate,
            selectedStockItems,
            selectedAgents,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        } = this;
        return JSON.stringify([
            selectedDateRange,
            selectedAsOfDate,
            selectedStockItems,
            selectedAgents,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        ]);
    }

    public async expensiveCalc() {
        const jobId = this.currentJobId;
        this.generateLoadingText(0.2, 0.3);

        const queryHead = await this._ensureTablesLoaded(
            ['sales', 'creditNotes', 'customerTrades', 'globalCustomers'] as any[],
        );
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.3, 0.9);

        const salesTable = getTableName('sales');
        const cnTable = getTableName('creditNotes');
        const arTable = getTableName('customerTrades');
        const globalTable = getTableName('globalCustomers');

        const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.oneYearBeforeRange[0]} AND ${this.oneYearBeforeRange[1]}`;
        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        const agentFilter = this.selectedAgents && this.selectedAgents.length > 0
            ? ` AND ${COL.AGENT} IN (${this.selectedAgents.map((a: string) => `'${escapeStr(a)}'`).join(', ')})`
            : '';

        // Run all rawQuery in parallel
        const [slResult, slCnResult, arResult, globalCountResult] = await Promise.all([
            queryHead.rawQuery(`
                SELECT
                    ${sqlSumByDocType(['IV', 'CS'], COL.AMOUNT, 'iv_cs_amount')},
                    ${sqlSumByDocType(['DN'], COL.AMOUNT, 'dn_amount')}
                FROM ${salesTable}
                WHERE ${dateFilter}${agentFilter}
            `),
            queryHead.rawQuery(`
                SELECT ${sqlSum(COL.AMOUNT, 'cn_amount')}
                FROM ${cnTable}
                WHERE ${dateFilter}${agentFilter}
            `),
            queryHead.rawQuery(`
                SELECT
                    ${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
                    ${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')},
                    ${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')}
                FROM ${arTable}
                WHERE ${dateFilter}${agentFilter}
            `),
            queryHead.rawQuery(`
                SELECT COUNT(*) as cnt
                FROM ${globalTable}
                WHERE TRY_CAST("FIRSTDEAL" AS BIGINT) IS NOT NULL
                    AND TRY_CAST("FIRSTDEAL" AS BIGINT) <= ${this.selectedAsOfDate}
                    ${agentFilter}
            `),
        ]);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.9, 0.95);

        // Extract values
        const slRow = slResult[0] || {};
        const slIvCsAmount = Number(slRow.iv_cs_amount) || 0;
        const slDnAmount = Number(slRow.dn_amount) || 0;

        const slCnRow = slCnResult[0] || {};
        const slCnAmount = Number(slCnRow.cn_amount) || 0;

        const arRow = arResult[0] || {};
        const arIvAmount = Number(arRow.iv_amount) || 0;
        const arCnAmount = Number(arRow.cn_amount) || 0;
        const arDnAmount = Number(arRow.dn_amount) || 0;

        this.customersCount = globalCountResult[0] ? Number(globalCountResult[0].cnt) || 0 : 0;

        let salesAmount: number;
        let activeCustomersCount: number;

        if (this.slPhModuleOnly) {
            salesAmount = slIvCsAmount
                - (this.includeCreditNote ? slCnAmount : 0)
                + (this.includeDebitNote ? slDnAmount : 0);

            const slCustomerRows = await queryHead.rawQuery(`
                SELECT COUNT(DISTINCT ${COL.CODE}) as customerCount
                FROM ${salesTable}
                WHERE ${dateFilter}${agentFilter}
            `);
            activeCustomersCount = slCustomerRows[0] ? Number(slCustomerRows[0].customerCount) || 0 : 0;
        } else {
            salesAmount = arIvAmount
                - (this.includeCreditNote ? arCnAmount : 0)
                + (this.includeDebitNote ? arDnAmount : 0);

            const arCustomerRows = await queryHead.rawQuery(`
                SELECT COUNT(DISTINCT ${COL.CODE}) as customerCount
                FROM ${arTable}
                WHERE ${dateFilter}${agentFilter}
            `);
            activeCustomersCount = arCustomerRows[0] ? Number(arCustomerRows[0].customerCount) || 0 : 0;
        }

        // Always use AR module for last invoice date
        const lastInvoiceDateRows = await queryHead.rawQuery(`
            SELECT MAX(TRY_CAST(${COL.DOCDATE} AS BIGINT)) as last_invoice_date
            FROM ${arTable}
            WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) <= ${this.selectedAsOfDate}${agentFilter}
        `);
        this.lastInvoiceDate = lastInvoiceDateRows[0] ? Number(lastInvoiceDateRows[0].last_invoice_date) || 0 : 0;

        this.activeCustomersCount = activeCustomersCount;
        this.salesAmount = salesAmount;

        this.generateLoadingText(1);
        this.saveHistory('salesAmount', 'lastInvoiceDate', 'activeCustomersCount', 'customersCount');
    }
}
